import { LocalStorageService } from '../configuration';

// Đọc quyền (permission) của admin đang đăng nhập từ claim "Permission" trong access token.
// Dùng để ẩn/hiện menu + chức năng CMS theo phân quyền (RBAC).
export const PERMISSION = {
  ContentModerate: 'Content.Moderate',
  FinanceView: 'Finance.View',
  WithdrawApprove: 'Withdraw.Approve',
  UserManage: 'User.Manage',
  RevealPii: 'User.RevealPii',
  CosmeticGrant: 'Cosmetic.Grant',
  CreatorApprove: 'Creator.Approve',
  DonateManage: 'Donate.Manage',
  RoleManage: 'Role.Manage',
  AdsManage: 'Ads.Manage',
  AffiliateManage: 'Affiliate.Manage',
  ArenaManage: 'Arena.Manage',
} as const;

const decodeJwt = (token: string): any => {
  const seg = token.split('.')[1];
  if (!seg) return {};
  const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(b64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(json);
};

// Vai trò (claim "Role") của admin đang đăng nhập.
export const getAdminRoles = (): string[] => {
  try {
    const token = LocalStorageService.getItem('cmsAccessToken') as string;
    if (!token) return [];
    const r = decodeJwt(token)?.Role;
    if (Array.isArray(r)) return r;
    return r ? [r] : [];
  } catch {
    return [];
  }
};

const STAFF_ROLES = ['Admin', 'SuperAdmin', 'Mod', 'Staff', 'Accountant', 'Monetization'];

// "Chỉ xem": token cms có vai trò Viewer và KHÔNG có vai trò nhân viên nào khác.
// STRICT: không có token / không đọc được vai trò => false. Chỉ để hiện UI; backend + gateway mới chặn thật.
export function isViewerOnly(): boolean {
  const roles = getAdminRoles().map((r) => String(r).toLowerCase());
  if (!roles.includes('viewer')) return false;
  return !STAFF_ROLES.some((r) => roles.includes(r.toLowerCase()));
}

// true nếu là Admin/SuperAdmin. Dùng cho khu vực cấu hình & giám sát hệ thống
// (Settings, Rà truy vấn) — Kiểm duyệt viên / Kế toán không được thấy.
// FAIL-OPEN khi không đọc được vai trò (giống hasAnyPermission): backend là lớp chặn thật.
export const isAdminOrAbove = (): boolean => {
  const roles = getAdminRoles();
  if (roles.length === 0) return true;
  if (isViewerOnly()) return true; // chỉ xem: thấy mọi menu để designer vẽ lại (máy chủ chặn thao tác)
  return roles.includes('Admin') || roles.includes('SuperAdmin');
};

// true nếu vai trò là SuperAdmin — dùng để ẩn/hiện "Khoá thủ công"/"Gỡ khoá" tài khoản doanh nghiệp
// (M4, chỉ SuperAdmin — xem AdminBrandMarketplaceController). FAIL-OPEN như `isAdminOrAbove` khi không
// đọc được vai trò: backend vẫn chặn thật (403 + thông báo), FE chỉ ẩn bớt thao tác cho gọn.
export const isSuperAdmin = (): boolean => {
  const roles = getAdminRoles();
  if (roles.length === 0) return true;
  return roles.includes('SuperAdmin');
};

export const getAdminPermissions = (): string[] => {
  try {
    const token = LocalStorageService.getItem('cmsAccessToken') as string;
    if (!token) return [];
    const payload = decodeJwt(token);
    const p = payload?.Permission;
    if (Array.isArray(p)) return p;
    return p ? [p] : [];
  } catch {
    return [];
  }
};

// true nếu admin có ÍT NHẤT một trong các quyền truyền vào.
// FAIL-OPEN khi KHÔNG có claim quyền nào (token cũ phát trước khi có RBAC, hoặc lỗi decode):
// không ẩn menu để admin hiện tại khỏi bị mất sạch menu — backend mới là lớp CHẶN thật.
export const hasAnyPermission = (...required: string[]): boolean => {
  const mine = getAdminPermissions();
  if (mine.length === 0) return true;
  return required.some((r) => mine.includes(r));
};

// STRICT: KHÔNG fail-open. Chỉ true khi token thực sự có quyền tương ứng.
// Dùng cho khu vực nhạy cảm (Cab Ads / Affiliate): mặc định ẩn hẳn với mọi admin
// chưa được cấp quyền — kể cả token cũ không có claim quyền (tránh thao tác nhầm / hacker dò).
export const hasStrictPermission = (...required: string[]): boolean => {
  if (isViewerOnly()) return true; // chỉ xem: xem được mọi trang, không có quyền thao tác nào
  const mine = getAdminPermissions();
  return required.some((r) => mine.includes(r));
};
